// Centralized localStorage cache service for reference data
// Data is cached with a timestamp and can expire after a configurable duration

const CACHE_PREFIX = 'vivosign_cache_';
const DEFAULT_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

// Cache keys
export const CACHE_KEYS = {
  CATEGORIES: `${CACHE_PREFIX}categories`,
  USERS: `${CACHE_PREFIX}users`,
  RECIPIENTS: `${CACHE_PREFIX}recipients`,
} as const;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * Store data in localStorage with a timestamp
 */
export function setCache<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = { data, timestamp: Date.now() };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (e) {
    console.error(`[cacheService] Error setting cache for "${key}":`, e);
  }
}

/**
 * Retrieve cached data from localStorage if it exists and hasn't expired
 */
export function getCache<T>(key: string, maxAgeMs: number = DEFAULT_MAX_AGE_MS): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() - entry.timestamp > maxAgeMs) {
      // Expired — remove stale entry
      localStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch (e) {
    console.error(`[cacheService] Error reading cache for "${key}":`, e);
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Remove a specific cache entry
 */
export function clearCache(key: string): void {
  localStorage.removeItem(key);
}

/**
 * Remove all app cache entries (used on logout / session expiry)
 */
export function clearAllCache(): void {
  Object.values(CACHE_KEYS).forEach((key) => {
    localStorage.removeItem(key);
  });
}
